import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import PageLayout from '../../components/layout/PageLayout';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { supabaseHelpers } from '../../lib/supabase';

const History = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [uploadingProjectId, setUploadingProjectId] = useState(null);

  useEffect(() => {
    const load = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }
      try {
        const { data, error } = await supabaseHelpers?.getUserProjects(user?.id);
        if (!error) setProjects(data || []);
      } catch (error) {
        console.error('Erro ao buscar projetos:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  const handleUploadFiles = async (projectId, files) => {
    if (!user?.id || !files?.length) return;
    setUploadingProjectId(projectId);
    try {
      const bucket = 'project-files';
      const uploaded = [];
      for (const file of Array.from(files)) {
        const safeName = file?.name?.replace(/\s+/g, '_');
        const filePath = `${user?.id}/${projectId}/${Date.now()}_${safeName}`;
        const { data, error } = await supabaseHelpers?.uploadFile(bucket, filePath, file);
        if (!error && data?.path) {
          const { data: signed } = await supabaseHelpers?.getSignedUrl(bucket, filePath, 3600);
          uploaded.push({ path: filePath, url: signed?.signedUrl });
        }
      }
      if (uploaded?.length > 0) {
        alert(`Upload concluído (${uploaded?.length} arquivo(s)).`);
      }
    } catch (error) {
      console.error('Erro ao enviar arquivos do projeto:', error);
      alert('Falha no upload de arquivos do projeto. Verifique o bucket "project-files".');
    } finally {
      setUploadingProjectId(null);
    }
  };

  const handleRecalculate = (projectId) => {
    navigate(`/enhanced-cost-calculator?projectId=${projectId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <span className="text-lg text-foreground">Carregando histórico...</span>
        </div>
      </div>
    );
  }

  return (
    <PageLayout
      title="Histórico de Projetos"
      subtitle="Revise projetos anteriores, re-calcule e anexe arquivos."
      iconName="Projects"
      headerRight={(
        <Button
          variant="outline"
          iconName="Calculator"
          iconPosition="left"
          onClick={() => navigate('/calculator')}
        >
          Nova Calculadora
        </Button>
      )}
    >
      {projects?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mb-6">
            <Icon name="Projects" size={32} className="text-muted-foreground" />
          </div>
          <h3 className="text-xl font-semibold text-foreground mb-2">Nenhum projeto encontrado</h3>
          <p className="text-muted-foreground text-center mb-8 max-w-md">Faça um cálculo na calculadora e salve para aparecer aqui.</p>
          <Button onClick={() => navigate('/calculator')} iconName="Calculator" iconPosition="left" size="lg">
            Ir para Calculadora
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {projects?.map((project) => (
            <div key={project?.id} className="bg-card border border-border rounded-2xl p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-foreground text-lg">{project?.name || 'Projeto sem nome'}</h3>
                  <p className="text-muted-foreground text-sm">{new Date(project?.created_at)?.toLocaleString()}</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => handleRecalculate(project?.id)}>
                  <Icon name="Calculator" size={14} className="mr-2" /> Recalcular
                </Button>
              </div>

              {project?.printer && (
                <p className="text-sm text-muted-foreground mb-2">Impressora: {project?.printer?.brand} {project?.printer?.model}</p>
              )}
              <div className="text-sm text-muted-foreground mb-4">
                Filamentos: {project?.project_filaments?.length || 0}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-muted-foreground">Arquivos do projeto</span>
                </div>
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => handleUploadFiles(project?.id, e?.target?.files)}
                  />
                  <span className="px-3 py-2 border border-border rounded-lg text-sm hover:bg-muted">
                    {uploadingProjectId === project?.id ? 'Enviando...' : 'Enviar Arquivos'}
                  </span>
                </label>
              </div>
            </div>
          ))}
        </div>
      )}
    </PageLayout>
  );
};

export default History;